import {Button, Card, Checkbox, Choice, DataStatus} from 'platform/components';
import {Box, Grid, Hide, HStack, Right, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useRef, useState} from 'react';

import {always, groupBy, isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {BusinessCaseResponseBody, OrderResponseBody} from '@dms/api/finance';
import {
  BusinessCaseDocumentsSettingsResponseBody,
  CheckoutSubEntityClass,
  useGetBusinessCaseDocumentsSettingsQuery,
  useGetCheckoutHandoverDocumentTypesQuery,
  useGetSelectedCheckoutOrderHandoverDocumentsQuery,
  useGetVehicleQuery,
  useSelectCheckoutOrderHandoverDocumentsMutation,
} from '@dms/api/sales';
import {NoteV2ResponseBody} from '@dms/api/shared';
import {useGetSaleVehicleFromSaleVehicleIdQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {
  DocumentTemplateBox,
  handleApiError,
  TemplateBoxTemplate,
  TemplateWithPrimary,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  useTenant,
} from '@dms/shared';
import {FileData, NoteRequestBody} from '@dms/teas';

import {convertStringToCamelCase, suffixTestId, TestIdProps} from 'shared/web';

import {CheckoutTemplateReturnType} from './hooks/useGetCheckoutDocumentTemplates';

export interface DocumentsProps {
  readonly?: boolean;
  templates: CheckoutTemplateReturnType[];
  documents?: FileData[];
  documentNotes?: {[index: string]: NoteV2ResponseBody | undefined};
  handleCreateDocuments: (documentsByType: Record<string, DocumentTemplateState[]>) => void;
  onNoteChange?: (documentType: CheckoutSubEntityClass, note: NoteRequestBody) => void;
  userPickedLocale: string | null;
  onLocaleChange: (locale: string | null) => void;
  isLoading?: boolean;
  emptySubHeadline?: string;
  isCreateDisabled?: boolean;
  businessCase?: BusinessCaseResponseBody;
  order: OrderResponseBody;
}

const documentNoteSubEntityClass: {[index: string]: CheckoutSubEntityClass} = {
  'purchase-contract': 'PURCHASE_CONTRACT',
  'purchase-brokerage-contract': 'PURCHASE_BROKERAGE_CONTRACT',
  'sale-contract': 'SALE_CONTRACT',
  'sale-brokerage-contract': 'SALE_BROKERAGE_CONTRACT',
};

const NOTE_DEBOUNCE_TIMEOUT_TIME = 1000;
export type DocumentTemplateState = CheckoutTemplateReturnType & {
  title: string;
  isSelected: boolean;
  selectedTemplate: TemplateBoxTemplate | undefined;
  note: NoteV2ResponseBody | undefined;
};

const hasAvailableTemplate = (
  document: Pick<DocumentTemplateState, 'templates' | 'authorizationTemplates'>
) => isNotNilOrEmpty(document.templates) || isNotNilOrEmpty(document.authorizationTemplates);

export const Documents = ({
  templates,
  documentNotes = {},
  handleCreateDocuments,
  isLoading,
  readonly,
  isCreateDisabled,
  businessCase,
  userPickedLocale,
  onLocaleChange,
  ...props
}: DocumentsProps & TestIdProps) => {
  const [documentTemplates, setDocumentTemplates] = useState<DocumentTemplateState[]>([]);
  const noteChangeDebounceTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const {data: bcDocumentsSettings, isLoading: isBcDocumentsSettingsLoading} =
    useGetBusinessCaseDocumentsSettingsQuery();

  const isAnyTemplateSelected = documentTemplates.some(
    (template) => template.isSelected && hasAvailableTemplate(template)
  );
  const {data: saleVehicle} = useGetSaleVehicleFromSaleVehicleIdQuery(
    {
      vehicleId: businessCase?.saleVehicleId ?? '',
    },
    {skip: isNilOrEmpty(businessCase?.saleVehicleId)}
  );
  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: saleVehicle?.vehicleId ?? ''},
    {skip: isNilOrEmpty(saleVehicle?.vehicleId)}
  );
  const {isTenantCountrySupported, isMultiLocale, supportedLocales, tenant} = useTenant();
  const isBelgianTenant = isTenantCountrySupported('BEL');
  const {data: customerData} = useGetCustomerV2Query(
    {customerId: businessCase?.customerId ?? ''},
    {skip: isNilOrEmpty(businessCase?.customerId) || !isMultiLocale}
  );
  const selectedLocale =
    userPickedLocale ?? customerData?.preferredLocale ?? tenant?.defaultLocale ?? null;
  const isLocaleMatch = (locale: string | null | undefined) =>
    isNil(locale) || locale === selectedLocale;
  const shouldShowHandoverDocs =
    isBelgianTenant && businessCase?.businessCaseInternalType === 'SELLING';
  const {data: selectedCheckoutOrderHandoverDocuments} =
    useGetSelectedCheckoutOrderHandoverDocumentsQuery(
      {
        businessCaseId: businessCase?.id ?? '',
        orderId: props.order.id ?? '',
      },
      {skip: !shouldShowHandoverDocs}
    );
  const {
    data: checkoutHandoverDocumentTypes,
    isLoading: isCheckoutHandoverDocumentTypesQueryLoading,
  } = useGetCheckoutHandoverDocumentTypesQuery(undefined, {skip: !shouldShowHandoverDocs});
  const [selectCheckoutOrderHandoverDocuments] = useSelectCheckoutOrderHandoverDocumentsMutation();
  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();

  useEffect(
    function syncDocumentTemplates() {
      if (!isBcDocumentsSettingsLoading) {
        const newDocumentTemplates = templates.map((template) => {
          const filteredTemplates = isMultiLocale
            ? template.templates.filter((option) => isLocaleMatch(option.locale))
            : template.templates;
          const filteredAuthTemplates = isMultiLocale
            ? template.authorizationTemplates?.filter((option) => isLocaleMatch(option.locale))
            : template.authorizationTemplates;
          const existing = documentTemplates.find(
            (document) =>
              (document.id && document.id === template.id) ||
              (!document.id && document.type === template.type)
          );
          const isExistingSelectedStillValid =
            existing?.selectedTemplate &&
            (filteredTemplates.some((t) => t.id === existing.selectedTemplate?.id) ||
              filteredAuthTemplates?.some((t) => t.id === existing.selectedTemplate?.id));
          const resolvedSelectedTemplate = isExistingSelectedStillValid
            ? existing?.selectedTemplate
            : (filteredAuthTemplates?.find((t) => t.isPrimary) ??
              filteredTemplates.find((t) => t.isPrimary) ??
              filteredAuthTemplates?.[0] ??
              filteredTemplates[0] ??
              template.primaryTemplate);

          return {
            ...template,
            templates: filteredTemplates,
            authorizationTemplates: filteredAuthTemplates,
            title:
              template.title ||
              i18n.t(`entity.document.labels.${convertStringToCamelCase(template.type)}`),
            isSelected:
              existing?.isSelected ??
              (businessCase && bcDocumentsSettings
                ? getIsSelectedOption(businessCase, template.type, bcDocumentsSettings)
                : true),
            selectedTemplate: resolvedSelectedTemplate,
            note: documentNotes[documentNoteSubEntityClass[template.type]],
          };
        });

        setDocumentTemplates(newDocumentTemplates);
      }
      // Dependency `documentTemplates` is omitted intentionally. Effect should run only when templates or selectedLocale changed.
    },
    // eslint-disable-next-line react/exhaustive-deps
    [templates, isBcDocumentsSettingsLoading, selectedLocale, isMultiLocale]
  );

  const onNoteChange = (subEntityClass: CheckoutSubEntityClass) => (note: string | null) => {
    if (noteChangeDebounceTimeout.current) {
      clearTimeout(noteChangeDebounceTimeout.current);
    }

    noteChangeDebounceTimeout.current = setTimeout(() => {
      const body: NoteRequestBody = {
        note: note ?? '',
      };

      props.onNoteChange?.(subEntityClass, body);
    }, NOTE_DEBOUNCE_TIMEOUT_TIME);
  };

  const onSelect = (template: DocumentTemplateState) => (isSelected: boolean) => {
    setDocumentTemplates(
      documentTemplates.map((document) => ({
        ...document,
        isSelected:
          (document.id && template.id === document.id) ||
          (!document.id && template.type === document.type)
            ? isSelected
            : document.isSelected,
      }))
    );
  };

  const onTemplateChange =
    (template: DocumentTemplateState) => (selectedTemplate: TemplateBoxTemplate) => {
      setDocumentTemplates(
        documentTemplates.map((document) => ({
          ...document,
          selectedTemplate:
            (document.id && template.id === document.id) ||
            (!document.id && template.type === document.type)
              ? (selectedTemplate as TemplateWithPrimary)
              : document.selectedTemplate,
        }))
      );
    };

  const handleCreate = () => {
    const selected = documentTemplates?.filter(
      (template) => !!template.isSelected && hasAvailableTemplate(template)
    );
    const documentsByType = groupBy((document) => document.type, selected) as Record<
      string,
      DocumentTemplateState[]
    >;

    handleCreateDocuments(documentsByType);
  };

  const handleToggleHandoverDocument = (documentKey: string) => {
    const currentSelection = selectedCheckoutOrderHandoverDocuments?.handoverDocuments ?? [];
    const updatedSelection = currentSelection.includes(documentKey)
      ? currentSelection.filter((key: string) => key !== documentKey)
      : [...currentSelection, documentKey];

    selectCheckoutOrderHandoverDocuments({
      businessCaseId: businessCase?.id ?? '',
      orderId: props.order.id,
      orderHandoverDocumentsRequestBody: {
        handoverDocuments: updatedSelection,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const ITEMS_PER_COLUMN_HANDOVER_DOCS = Math.ceil(
    (checkoutHandoverDocumentTypes?.length ?? 0) / 2
  );
  const leftColumnHandoverDocsData =
    checkoutHandoverDocumentTypes?.slice(0, ITEMS_PER_COLUMN_HANDOVER_DOCS) ?? [];
  const rightColumnHandoverDocsData =
    checkoutHandoverDocumentTypes?.slice(ITEMS_PER_COLUMN_HANDOVER_DOCS) ?? [];

  return (
    <Grid columns={1}>
      <Hide when={readonly}>
        <Show when={shouldShowHandoverDocs}>
          <Card variant="inlineGrey">
            <DataStatus isLoading={isCheckoutHandoverDocumentTypesQueryLoading}>
              <VStack spacing={3}>
                <Text size="small" color="tertiary">
                  {i18n.t('entity.checkout.labels.handoverDocuments')}
                </Text>
                <Grid columns={2}>
                  <VStack spacing={2}>
                    {leftColumnHandoverDocsData.map((documentType) => (
                      <HStack key={documentType.key} spacing={2}>
                        <Checkbox
                          value={
                            selectedCheckoutOrderHandoverDocuments?.handoverDocuments?.includes(
                              documentType.key
                            ) ?? false
                          }
                          onChange={() => {
                            handleToggleHandoverDocument(documentType.key);
                          }}
                        />
                        <Text size="small">{documentType.label}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <VStack spacing={2}>
                    {rightColumnHandoverDocsData.map((documentType) => (
                      <HStack key={documentType.key} spacing={2}>
                        <Checkbox
                          value={
                            selectedCheckoutOrderHandoverDocuments?.handoverDocuments?.includes(
                              documentType.key
                            ) ?? false
                          }
                          onChange={() => {
                            handleToggleHandoverDocument(documentType.key);
                          }}
                        />
                        <Text size="small">{documentType.label}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Grid>
              </VStack>
            </DataStatus>
          </Card>
        </Show>
        <Text size="small" color="tertiary">
          {i18n.t('general.labels.selectDocumentsInformation')}
        </Text>
        <Show when={isMultiLocale}>
          <Card variant="inlineGrey" colorStripe="blue">
            <HStack align="center" spacing={3}>
              <Text size="small">{i18n.t('entity.user.labels.langAndTime')}:</Text>
              <Box width={50}>
                <Choice
                  value={selectedLocale}
                  onChange={(value) => onLocaleChange(value)}
                  options={supportedLocales}
                  isNotClearable
                  data-testid={suffixTestId('language-select', props)}
                />
              </Box>
            </HStack>
          </Card>
        </Show>
        <DataStatus isLoading={isBcDocumentsSettingsLoading}>
          <VStack spacing={4}>
            {documentTemplates.map((document) => (
              <DocumentTemplateBox
                type={document.type}
                title={document.title}
                isSelected={document.isSelected}
                selectedTemplate={document.selectedTemplate || null}
                templates={document.templates}
                authorizationTemplates={document.authorizationTemplates}
                authorizationProfileLabel={document.authorizationProfileLabel}
                onSelect={onSelect(document)}
                onTemplateChange={onTemplateChange(document)}
                isWithNote={Object.keys(documentNoteSubEntityClass).includes(document.type)}
                noteLabel={i18n.t('entity.document.labels.contractNote')}
                onNoteChange={onNoteChange(documentNoteSubEntityClass[document.type])}
                key={`${document.id ?? 'noId'}-${document.type}`}
                data-testid={suffixTestId(
                  `templates-${convertStringToCamelCase(document.type)}`,
                  props
                )}
              />
            ))}
          </VStack>
        </DataStatus>
        <Right>
          {/* TODO: tooltip for disabled button cuz of perms */}
          <Button
            data-testid={suffixTestId('create', props)}
            isDisabled={!isAnyTemplateSelected || isCreateDisabled}
            isLoading={isLoading}
            onClick={() => {
              handleCreate();
              handleSearchAuthorizationProfiles(
                {
                  vehicleType: vehicleData?.vehicle?.type ?? null,
                  vehicleMake: vehicleData?.vehicle?.make ?? '',
                  vehicleModelFamily: vehicleData?.vehicle?.modelFamily ?? null,
                  vehicleModelFamilyGroup: vehicleData?.vehicle?.modelFamilyGroup ?? null,
                },
                businessCase?.customerId,
                {bcNumber: businessCase?.code, bcCreatedAt: businessCase?.createdAt}
              );
            }}
            title={i18n.t('general.action.createDocuments')}
          />
        </Right>
      </Hide>
    </Grid>
  );
};

const getIsSelectedOption = (
  businessCase: BusinessCaseResponseBody,
  templateType: string,
  savedDocumentsOptions: BusinessCaseDocumentsSettingsResponseBody
) => {
  if (
    businessCase.businessCaseInternalType === 'SELLING' &&
    isNotNilOrEmpty(businessCase.brokerageBusinessCaseId)
  ) {
    // selling brokerage
    return match<string, boolean>(templateType)
      .with('vehicle-condition', always(savedDocumentsOptions.sellingBrokerage.vehicleCondition))
      .with('power-of-attorney', always(savedDocumentsOptions.sellingBrokerage.powerOfAttorney))
      .with(
        'sale-handover-protocol',
        always(savedDocumentsOptions.sellingBrokerage.saleHandoverProtocol)
      )
      .with(
        'sale-brokerage-contract',
        always(savedDocumentsOptions.sellingBrokerage.saleBrokerageContract)
      )
      .with('GDPR_MARKETING_CONSENT', always(savedDocumentsOptions.sellingBrokerage.consent))
      .otherwise(always(true));
  }
  if (businessCase.businessCaseInternalType === 'SELLING') {
    // selling
    return match<string, boolean>(templateType)
      .with('vehicle-condition', always(savedDocumentsOptions.selling.vehicleCondition))
      .with('power-of-attorney', always(savedDocumentsOptions.selling.powerOfAttorney))
      .with('sale-handover-protocol', always(savedDocumentsOptions.selling.saleHandoverProtocol))
      .with('sale-contract', always(savedDocumentsOptions.selling.saleContract))
      .with('GDPR_MARKETING_CONSENT', always(savedDocumentsOptions.selling.consent))
      .otherwise(always(true));
  }
  if (businessCase.businessCaseInternalType === 'BUYING') {
    // buying
    return match<string, boolean>(templateType)
      .with('vehicle-condition', always(savedDocumentsOptions.buying.vehicleCondition))
      .with('power-of-attorney', always(savedDocumentsOptions.buying.powerOfAttorney))
      .with('handover-protocol', always(savedDocumentsOptions.buying.handoverProtocol))
      .with('purchase-contract', always(savedDocumentsOptions.buying.purchaseContract))
      .with('GDPR_MARKETING_CONSENT', always(savedDocumentsOptions.buying.consent))
      .otherwise(always(true));
  }
  if (businessCase.businessCaseInternalType === 'PURCHASE_BROKERAGE') {
    return match<string, boolean>(templateType)
      .with('vehicle-condition', always(savedDocumentsOptions.purchaseBrokerage.vehicleCondition))
      .with('power-of-attorney', always(savedDocumentsOptions.purchaseBrokerage.powerOfAttorney))
      .with(
        'purchase-brokerage-handover-protocol',
        always(savedDocumentsOptions.purchaseBrokerage.purchaseBrokerageHandoverProtocol)
      )
      .with(
        'purchase-brokerage-contract',
        always(savedDocumentsOptions.purchaseBrokerage.purchaseBrokerageContract)
      )
      .with('GDPR_MARKETING_CONSENT', always(savedDocumentsOptions.purchaseBrokerage.consent))
      .otherwise(always(true));
  }
  return true;
};
